/**
 * Paying rent now, from either dashboard (COL-533).
 *
 * Two rails, matching V1:
 *
 * - With an ACTIVE SEPA mandate, "Pay now" debits immediately through
 *   `create-rent-payment` rather than opening a card checkout — the tenant
 *   has already authorised us, so sending them to Stripe would charge a
 *   second instrument.
 * - Otherwise it opens Stripe Checkout through `create-card-checkout`.
 *
 * The AMOUNT is deliberately the BILLED amount, not the remaining balance.
 * `create-rent-payment` subtracts `received_cents` itself (COL-417 review
 * fix, index.ts:177-187) and answers `alreadyPaid` when nothing is open, so
 * the server owns the clamp. A client that pre-subtracted would be a second
 * opinion on how much a tenant owes, and the two could disagree.
 */

import { supabase } from "@/lib/supabase";

export type PayNowInput = {
  bookingId: number;
  amountCents: number;
  /** `YYYY-MM-DD`; the row's due date, or the next rent date. */
  dueDate: string;
  /**
   * OPTIONAL, and omit it when paying an EXISTING row.
   *
   * `create-rent-payment` writes `description ?? mutable.description`, so
   * anything sent here RENAMES the row the tenant already has. V2 passed its
   * UI heading, so every Pay now rewrote "Miete Oktober 2026" to "Next
   * payment" -- and the tenant's own payment history lost which month each
   * row was for. Leave it unset and the stored description survives; the
   * function still names a row it has to create itself.
   */
  description?: string;
};

/** What the SEPA debit actually did — each needs different wording. */
export type SepaPayNowOutcome =
  | "collected"
  /** Nothing open against this month. */
  | "alreadyPaid"
  /**
   * A debit is already at the bank for this month. Saying "collected" here
   * would report a second collection that never happened.
   */
  | "alreadyInFlight";

export async function payRentNowViaSepa(
  input: PayNowInput
): Promise<SepaPayNowOutcome> {
  const { data, error } = await supabase.functions.invoke(
    "create-rent-payment",
    {
      body: {
        bookingId: input.bookingId,
        amountCents: input.amountCents,
        currency: "eur",
        type: "rent",
        dueDate: input.dueDate,
        // Spread, not `description: input.description` -- sending the key as
        // `undefined` is not the same as not sending it.
        ...(input.description ? { description: input.description } : {}),
      },
    }
  );
  if (error) throw error;
  const body = data as Record<string, unknown> | null;
  if (body?.alreadyPaid) return "alreadyPaid";
  if (body?.alreadyInFlight) return "alreadyInFlight";
  return "collected";
}

/**
 * Start Stripe Checkout and return the URL to send the tenant to.
 *
 * Returns the URL rather than navigating, so the caller decides when to
 * leave the page — and so this stays testable without stubbing `location`.
 * Null when the session could not be created.
 */
export async function createCardCheckoutUrl(
  input: PayNowInput & {
    paymentId?: string | number;
    type?: "rent" | "deposit";
  }
): Promise<string | null> {
  const origin = window.location.origin;
  const { data, error } = await supabase.functions.invoke(
    "create-card-checkout",
    {
      body: {
        bookingId: input.bookingId,
        // Omit when paying the deposit: there is often no pre-created deposit
        // row, so the function self-computes the expected amount from the
        // booking and `confirm-payment-from-session` creates/recovers the row
        // after Stripe (COL-291). Spread, not an `undefined` key.
        ...(input.paymentId != null ? { paymentId: input.paymentId } : {}),
        dueDate: input.dueDate,
        amountCents: input.amountCents,
        currency: "eur",
        type: input.type ?? "rent",
        ...(input.description ? { description: input.description } : {}),
        successUrl: `${origin}/dashboard?payment=success`,
        cancelUrl: `${origin}/dashboard?payment=canceled`,
      },
    }
  );
  if (error) throw error;
  const url = (data as Record<string, unknown> | null)?.checkoutUrl;
  return typeof url === "string" && url ? url : null;
}
